import type { Query, QueryClient } from '@tanstack/react-query';

export const SHOP_SWITCH_MOTION = { reveal: 0.18, sign: 0.6, loading: 1.4 } as const;
export const SHOP_SWITCH_SLOW_MS = 8_000;
export const SHOP_SWITCH_TIMEOUT_MS = 20_000;

// Some legacy queries omit shopId. Only the authenticated identity can safely
// survive a switch; its provider lives outside the remounted application.
export const isShopQuery = (query: Query) => query.queryKey[0] !== 'auth';

/**
 * Wait for first results, not polling cycles. Page/widget errors belong to their
 * own error UI: they must never turn a successfully resolved shop into a failed
 * switch. The provider already validates shop identity and access before mount.
 */
export function waitForShopQueries(client: QueryClient, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const settled = new Set<string>();
    let frame = 0;
    let stableFrames = 0;
    const cleanup = () => {
      cancelAnimationFrame(frame);
      unsubscribe();
      signal.removeEventListener('abort', abort);
    };
    const abort = () => {
      cleanup();
      reject(new Error('Switch cancelled'));
    };
    const check = () => {
      if (signal.aborted) {
        abort();
        return;
      }
      let pending = false;
      for (const query of client
        .getQueryCache()
        .findAll({ predicate: isShopQuery, type: 'active' })) {
        if (settled.has(query.queryHash)) continue;
        if (query.state.fetchStatus !== 'idle' || query.state.status === 'pending') pending = true;
        else settled.add(query.queryHash);
      }
      stableFrames = pending ? 0 : stableFrames + 1;
      // Two paints allow mounted effects and dependent queries to subscribe.
      if (stableFrames >= 2) {
        cleanup();
        resolve();
        return;
      }
      frame = requestAnimationFrame(check);
    };
    const unsubscribe = client.getQueryCache().subscribe(() => {
      stableFrames = 0;
    });
    signal.addEventListener('abort', abort, { once: true });
    frame = requestAnimationFrame(check);
  });
}
